import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axiosInstance from "../../../services/axiosInstance";

// ── palette (always dark) ─────────────────────────────────────────────────────
const BG         = "#070B14";
const CARD_BG    = "rgba(255,255,255,0.05)";
const CARD_BR    = "rgba(255,255,255,0.09)";
const HDR_BG     = "rgba(255,255,255,0.03)";
const HDR_BR     = "rgba(255,255,255,0.08)";
const TEXT_PRI   = "#F0F4FF";
const TEXT_SEC   = "#94A3B8";
const TEXT_DIM   = "#4B5563";

const STATUS_CFG = {
  on_call:   { color: "#6366F1", bg: "rgba(99,102,241,0.15)", label: "ON CALL"   },
  ringing:   { color: "#F59E0B", bg: "rgba(245,158,11,0.13)",  label: "RINGING"  },
  available: { color: "#22C55E", bg: "rgba(34,197,94,0.12)",   label: "AVAILABLE"},
  offline:   { color: "#374151", bg: "rgba(255,255,255,0.03)", label: "OFFLINE"  },
  // The agent's own choice (Stage 4.8f.3, F2): not rung for customers.
  // (hex = the --warning / --danger tokens, on this dark board's own translucent backgrounds)
  break:     { color: "#E8930C", bg: "rgba(232,147,12,0.13)", label: "ON A BREAK" },
  dnd:       { color: "#E5484D", bg: "rgba(229,72,77,0.13)",  label: "DO NOT DISTURB" },
};

const WAIT_WARN = 60;
const WAIT_CRIT = 120;
const POLL_MS   = 2500;

// ── helpers ───────────────────────────────────────────────────────────────────
function waitColor(sec) {
  if (sec >= WAIT_CRIT) return "#EF4444";
  if (sec >= WAIT_WARN) return "#F59E0B";
  return "#22C55E";
}

function fmtDur(sec) {
  if (!sec || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtClock(now) {
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function fmtDate(now) {
  return now.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

function liveSec(sinceMs, clockOffset) {
  if (!sinceMs) return 0;
  return Math.max(0, Math.floor((Date.now() + clockOffset - sinceMs) / 1000));
}

function liveDur(sinceMs, durationSec, receivedAt, clockOffset) {
  if (durationSec != null && durationSec >= 0)
    return Math.max(0, Math.floor(durationSec + (Date.now() - receivedAt) / 1000));
  return liveSec(sinceMs, clockOffset);
}

function callerWaitSec(callers, clockOffset) {
  if (!callers?.length) return 0;
  return callers.reduce((mx, c) => Math.max(mx, liveSec(c.enqueuedAt, clockOffset)), 0);
}

// ── sub-components ────────────────────────────────────────────────────────────
function StatBox({ label, value, color }) {
  return (
    <div style={{
      background: CARD_BG, border: `1px solid ${CARD_BR}`,
      borderRadius: 14, padding: "18px 28px", minWidth: 130, textAlign: "center",
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: TEXT_SEC, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.03em", color: color || TEXT_PRI }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

const STATUS_TKEY = { on_call: "statusOnCall", ringing: "statusRinging", available: "statusAvailable", offline: "statusOffline", break: "statusOnBreak", dnd: "statusDnd" };

function AgentCard({ ag, receivedAt, clockOffset, tick }) {
  const { t }    = useTranslation("wallboard");
  const cfg      = STATUS_CFG[ag.status] || STATUS_CFG.offline;
  const isActive = ag.status === "on_call" || ag.status === "ringing";
  const dur = isActive
    ? fmtDur(liveDur(ag.since, ag.durationSec, receivedAt, clockOffset))
    : ag.status === "available"
    ? `${fmtDur(liveSec(ag.since, clockOffset))} ${t("idle")}`
    : null;

  return (
    <div style={{
      background: cfg.bg,
      border: `1.5px solid ${isActive ? cfg.color : "rgba(255,255,255,0.07)"}`,
      borderRadius: 14, padding: "14px 16px", minWidth: 130, flex: "0 0 auto",
      display: "flex", flexDirection: "column", gap: 5,
      boxShadow: isActive ? `0 0 18px ${cfg.color}28` : "none",
      transition: "box-shadow 0.4s",
    }}>
      {/* Extension + dot */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 17, color: TEXT_PRI, letterSpacing: "0.04em" }}>
          {ag.extension}
        </span>
        <span style={{
          width: 9, height: 9, borderRadius: "50%", background: cfg.color, flexShrink: 0,
          animation: isActive ? "pulse 1.4s ease-in-out infinite" : "none",
        }} />
      </div>
      {/* Section */}
      <div style={{ fontSize: 11, color: TEXT_SEC, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {ag.section || "—"}
      </div>
      {/* Status */}
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.09em", color: cfg.color }}>
        {t(STATUS_TKEY[ag.status] || "statusOffline", { defaultValue: cfg.label })}
      </div>
      {/* Duration */}
      {dur && (
        <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: isActive ? cfg.color : TEXT_SEC, letterSpacing: "0.01em" }}>
          {dur}
        </div>
      )}
    </div>
  );
}

function QueueRow({ q, clockOffset, tick }) {
  const { t }   = useTranslation("wallboard");
  const waiting = q.callers?.length ?? (q.callersWaiting || 0);
  const longest = callerWaitSec(q.callers, clockOffset) || (q.longestWaitSec || 0);
  const wc      = waitColor(longest);
  const barPct  = Math.min(100, (longest / WAIT_CRIT) * 100);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 16,
      padding: "14px 20px",
      borderBottom: `1px solid rgba(255,255,255,0.05)`,
    }}>
      {/* Section name */}
      <div style={{ flex: "0 0 160px", fontSize: 15, fontWeight: 700, color: TEXT_PRI, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {q.section}
      </div>

      {/* Waiting count */}
      <div style={{ flex: "0 0 80px", textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", color: waiting > 0 ? wc : TEXT_DIM }}>
          {waiting}
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: TEXT_SEC, marginTop: 2 }}>
          {t("waiting")}
        </div>
      </div>

      {/* Wait bar + time */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: 3,
            width: `${barPct}%`,
            background: wc,
            transition: "width 0.6s ease, background 0.4s",
            boxShadow: waiting > 0 && longest > 0 ? `0 0 8px ${wc}80` : "none",
          }} />
        </div>
        <div style={{
          fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums",
          color: waiting > 0 ? wc : TEXT_DIM, minWidth: 52, textAlign: "right",
        }}>
          {waiting > 0 && longest > 0 ? fmtDur(longest) : "—"}
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Wallboard() {
  const { t }   = useTranslation("wallboard");
  const profile = useSelector((s) => s.profile?.profile);
  const companyName = profile?.languages?.map(l => profile?.locales?.[l]?.name).find(n => n?.trim()) || "Teleredo";

  const [data,       setData]       = useState(null);
  const [agentsList, setAgentsList] = useState(null);
  const [status,     setStatus]     = useState("loading");
  const [tick,       setTick]       = useState(0);
  const [now,        setNow]        = useState(new Date());

  const etagRef       = useRef(null);
  const agEtagRef     = useRef(null);
  const agRcvAtRef    = useRef(Date.now());
  const clockOffRef   = useRef(0);

  // Poll live/summary
  const pollSummary = useCallback(async () => {
    try {
      const headers = {};
      if (etagRef.current) headers["If-None-Match"] = etagRef.current;
      const res = await axiosInstance.get("/live/summary", {
        headers,
        validateStatus: (s) => s === 304 || (s >= 200 && s < 300),
      });
      const svt = parseInt(res.headers["x-server-time"] || "", 10);
      if (!isNaN(svt)) clockOffRef.current = svt - Date.now();
      if (res.status === 304) return;
      if (res.headers["etag"]) etagRef.current = res.headers["etag"];
      setData(res.data);
      setStatus("ok");
    } catch { setStatus("error"); }
  }, []);

  // Poll live/agents
  const pollAgents = useCallback(async () => {
    try {
      const headers = {};
      if (agEtagRef.current) headers["If-None-Match"] = agEtagRef.current;
      const res = await axiosInstance.get("/live/agents", {
        headers,
        validateStatus: (s) => s === 304 || (s >= 200 && s < 300),
      });
      const svt = parseInt(res.headers["x-server-time"] || "", 10);
      if (!isNaN(svt)) clockOffRef.current = svt - Date.now();
      if (res.status === 304) return;
      if (res.headers["etag"]) agEtagRef.current = res.headers["etag"];
      agRcvAtRef.current = Date.now();
      setAgentsList(res.data);
    } catch { /* silent */ }
  }, []);

  // Polling
  useEffect(() => {
    pollSummary(); pollAgents();
    const id = setInterval(() => { pollSummary(); pollAgents(); }, POLL_MS);
    const onVis = () => {
      if (!document.hidden) { pollSummary(); pollAgents(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [pollSummary, pollAgents]);

  // 1-second tick for counters + clock
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setNow(new Date());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const clockOffset = clockOffRef.current;
  const receivedAt  = agRcvAtRef.current;
  const agents      = agentsList?.agents || data?.agents || [];
  const queues      = data?.queues  || [];
  const totals      = data?.totals  || {};

  const onCall    = agents.filter((a) => a.status === "on_call").length;
  const ringing   = agents.filter((a) => a.status === "ringing").length;
  const available = agents.filter((a) => a.status === "available").length;

  const totalWaiting = queues.reduce((s, q) => s + (q.callers?.length ?? q.callersWaiting ?? 0), 0);
  const longestWait  = queues.reduce((mx, q) =>
    Math.max(mx, callerWaitSec(q.callers, clockOffset) || q.longestWaitSec || 0), 0);

  // Fullscreen toggle
  function toggleFullscreen() {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  }

  return (
    <div style={{
      minHeight: "100vh", background: BG, color: TEXT_PRI,
      display: "flex", flexDirection: "column",
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>
      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.3; transform: scale(0.65); }
        }
        * { box-sizing: border-box; }
      `}</style>

      {/* ── Header ── */}
      <div style={{
        background: HDR_BG, borderBottom: `1px solid ${HDR_BR}`,
        padding: "12px 28px",
        display: "flex", alignItems: "center", gap: 20,
      }}>
        {/* Live dot */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 9, height: 9, borderRadius: "50%",
            background: status === "error" ? "#F59E0B" : "#22C55E",
            animation: status !== "error" ? "pulse 1.4s ease-in-out infinite" : "none",
          }} />
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: status === "error" ? "#F59E0B" : "#22C55E" }}>
            {status === "error" ? t("reconnecting") : t("live")}
          </span>
        </div>

        {/* Company name */}
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: TEXT_PRI, flex: 1 }}>
          {companyName}
        </span>

        {/* Clock */}
        <span style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em", color: TEXT_PRI }}>
          {fmtClock(now)}
        </span>
        <span style={{ fontSize: 13, color: TEXT_SEC }}>
          {fmtDate(now)}
        </span>

        {/* Fullscreen button */}
        <button
          onClick={toggleFullscreen}
          style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: TEXT_SEC,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
          }}
        >
          ⛶
        </button>
      </div>

      {/* ── Stats bar ── */}
      <div style={{
        padding: "20px 28px",
        display: "flex", gap: 14, flexWrap: "wrap",
      }}>
        <StatBox label={t("statTotalAgents")}  value={agents.length}  color={TEXT_PRI} />
        <StatBox label={t("statOnCall")}       value={onCall}         color="#6366F1" />
        <StatBox label={t("statRinging")}      value={ringing}        color="#F59E0B" />
        <StatBox label={t("statAvailable")}    value={available}      color="#22C55E" />
        <StatBox label={t("statWaiting")}      value={totalWaiting}   color={totalWaiting > 0 ? waitColor(longestWait) : TEXT_DIM} />
        <StatBox label={t("statLongestWait")}  value={longestWait > 0 ? fmtDur(longestWait) : "—"} color={longestWait > 0 ? waitColor(longestWait) : TEXT_DIM} />
      </div>

      {/* ── Body: agents + queues ── */}
      <div style={{ flex: 1, display: "flex", gap: 0, padding: "0 28px 28px", minHeight: 0 }}>

        {/* Agents panel */}
        <div style={{ flex: 1, minWidth: 0, paddingRight: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: TEXT_SEC, marginBottom: 14 }}>
            {t("agentsHeader")}
          </div>
          {agents.length === 0 ? (
            <div style={{ color: TEXT_DIM, fontSize: 14, marginTop: 24 }}>
              {status === "loading" ? t("loadingAgents") : t("noAgentsOnline")}
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {[...agents]
                .sort((a, b) => {
                  const order = { on_call: 0, ringing: 1, available: 2, break: 3, dnd: 4, offline: 5 };
                  return (order[a.status] ?? 4) - (order[b.status] ?? 4);
                })
                .map((ag) => (
                  <AgentCard
                    key={ag.extension}
                    ag={ag}
                    receivedAt={receivedAt}
                    clockOffset={clockOffset}
                    tick={tick}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Queues panel */}
        <div style={{ flex: "0 0 380px", borderLeft: `1px solid rgba(255,255,255,0.07)`, paddingLeft: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: TEXT_SEC, marginBottom: 14 }}>
            {t("queuesHeader")}
          </div>
          {queues.length === 0 ? (
            <div style={{ color: TEXT_DIM, fontSize: 14, marginTop: 24 }}>
              {status === "loading" ? t("loadingQueues") : t("noActiveQueues")}
            </div>
          ) : (
            <div style={{
              background: CARD_BG, border: `1px solid ${CARD_BR}`,
              borderRadius: 14, overflow: "hidden",
            }}>
              {queues.map((q, i) => (
                <QueueRow
                  key={q.section || i}
                  q={q}
                  clockOffset={clockOffset}
                  tick={tick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        borderTop: `1px solid ${HDR_BR}`,
        padding: "10px 28px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <a
          href="/heycallcenter/sidebar"
          style={{ fontSize: 11, color: TEXT_DIM, textDecoration: "none", fontWeight: 600, letterSpacing: "0.03em" }}
        >
          {t("backToPortal")}
        </a>
        <span style={{ fontSize: 11, color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}>
          {t("lastUpdated")} {fmtClock(now)}
        </span>
      </div>
    </div>
  );
}
